import React, { useEffect, useRef, useState } from "react";

// Precomputed accent classes so Tailwind doesn't purge them
const accentMap = {
  teal:
    "focus:ring-teal-500 focus:border-teal-500",
  emerald:
    "focus:ring-emerald-500 focus:border-emerald-500",
  amber:
    "focus:ring-amber-500 focus:border-amber-500",
  blue:
    "focus:ring-blue-500 focus:border-blue-500",
  orange:
    "focus:ring-orange-500 focus:border-orange-500",
};

const parseFormikDateTime = (value) => {
  if (!value || typeof value !== "string") return null;

  const normalized = value.trim().replace(" ", "T").replace(/:\d{2}$/, "");
  const match = normalized.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/,
  );

  if (!match) return null;

  const [, yyyy, mm, dd, hourValue, minuteValue] = match;
  const hour24 = Number(hourValue);
  if (Number.isNaN(hour24)) return null;

  return {
    dd,
    mm,
    yyyy,
    hh: String(hour24 % 12 || 12).padStart(2, "0"),
    mi: minuteValue,
    period: hour24 >= 12 ? "PM" : "AM",
  };
};

const to24Hour = (hour12, period) => {
  const parsedHour = Number(hour12);
  if (Number.isNaN(parsedHour) || parsedHour < 1 || parsedHour > 12) {
    return null;
  }

  if (period === "AM") {
    return parsedHour === 12 ? 0 : parsedHour;
  }

  return parsedHour === 12 ? 12 : parsedHour + 12;
};

const getMaxDayForMonth = (yearValue, monthValue) => {
  const parsedYear = Number(yearValue);
  const parsedMonth = Number(monthValue);

  if (
    Number.isNaN(parsedYear) ||
    Number.isNaN(parsedMonth) ||
    parsedMonth < 1 ||
    parsedMonth > 12
  ) {
    return 31;
  }

  return new Date(parsedYear, parsedMonth, 0).getDate();
};

/**
 * DateTimeSegmentInput
 *
 * Renders DD / MM / YYYY at HH : MM AM/PM as segmented inputs.
 * - Typing auto-advances to the next segment when max length is reached.
 * - Backspace on an empty segment returns focus to the previous segment.
 * - Enter key is handled by the parent form's onKeyDown.
 * - Combines segments into a 24-hour ISO "yyyy-MM-ddTHH:mm" string so
 *   Formik / Yup validation and API payloads continue to work unchanged.
 */
export default function DateTimeSegmentInput({
  name,
  formikValue,
  setFieldValue,
  setFieldTouched,
  accent = "orange",
}) {
  const [dd, setDd] = useState("");
  const [mm, setMm] = useState("");
  const [yyyy, setYyyy] = useState("");
  const [hh, setHh] = useState("");
  const [mi, setMi] = useState("");
  const [period, setPeriod] = useState("AM");
  const lastSyncedValueRef = useRef("");

  const ddRef = useRef(null);
  const mmRef = useRef(null);
  const yyyyRef = useRef(null);
  const hhRef = useRef(null);
  const miRef = useRef(null);
  const periodRef = useRef(null);

  const ring = accentMap[accent] ?? accentMap.orange;

  useEffect(() => {
    if (!formikValue) {
      if (lastSyncedValueRef.current) {
        setDd("");
        setMm("");
        setYyyy("");
        setHh("");
        setMi("");
        setPeriod("AM");
        lastSyncedValueRef.current = "";
      }
      return;
    }

    const parsed = parseFormikDateTime(formikValue);
    if (!parsed) return;

    lastSyncedValueRef.current = formikValue;

    if (
      dd !== parsed.dd ||
      mm !== parsed.mm ||
      yyyy !== parsed.yyyy ||
      hh !== parsed.hh ||
      mi !== parsed.mi ||
      period !== parsed.period
    ) {
      setDd(parsed.dd);
      setMm(parsed.mm);
      setYyyy(parsed.yyyy);
      setHh(parsed.hh);
      setMi(parsed.mi);
      setPeriod(parsed.period);
    }
  }, [formikValue, dd, mm, yyyy, hh, mi, period]);

  useEffect(() => {
    if (dd && mm && yyyy.length === 4 && hh && mi && period) {
      const hour24 = to24Hour(hh, period);
      if (hour24 === null) {
        if (formikValue !== "") setFieldValue(name, "");
        return;
      }

      const iso = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}T${String(hour24).padStart(2, "0")}:${mi.padStart(2, "0")}`;
      if (formikValue !== iso) {
        lastSyncedValueRef.current = iso;
        setFieldValue(name, iso, true);
      }
      return;
    }

    if (formikValue !== "") {
      lastSyncedValueRef.current = "";
      setFieldValue(name, "", true);
    }
  }, [dd, mm, yyyy, hh, mi, period, name, setFieldValue, formikValue]);

  const segBase = `text-center bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition py-2.5 ${ring}`;

  const makeChangeHandler = (setter, maxLen, nextRef, isAllowed) => (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, maxLen);
    if (isAllowed && !isAllowed(raw)) return;
    setter(raw);
    if (raw.length === maxLen && nextRef?.current) nextRef.current.focus();
  };

  const makeKeyDownHandler = (prevRef) => (e) => {
    if (e.key === "Backspace" && e.target.value === "" && prevRef?.current) {
      e.preventDefault();
      prevRef.current.focus();
    }
  };

  const makeBlurPadHandler = (setter) => (e) => {
    const rawValue = e.target.value.replace(/\D/g, "");
    if (rawValue && rawValue.length === 1) {
      setter(rawValue.padStart(2, "0"));
    }
    if (setFieldTouched) {
      setFieldTouched(name, true, true);
    }
  };

  const handleBlur = () => {
    if (setFieldTouched) {
      setFieldTouched(name, true, true);
    }
  };

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <input
        ref={ddRef}
        type="text"
        inputMode="numeric"
        placeholder="DD"
        value={dd}
        maxLength={2}
        onChange={makeChangeHandler(setDd, 2, mmRef, (value) => {
          if (!value) return true;
          const day = Number(value);
          if (Number.isNaN(day) || day < 0) return false;
          if (value.length === 1) return true;
          if (day < 1) return false;
          return day <= getMaxDayForMonth(yyyy, mm);
        })}
        onKeyDown={makeKeyDownHandler(null)}
        onBlur={makeBlurPadHandler(setDd)}
        className={`w-11 ${segBase}`}
        aria-label="Day"
      />
      <span className="text-gray-500 select-none text-sm">/</span>
      <input
        ref={mmRef}
        type="text"
        inputMode="numeric"
        placeholder="MM"
        value={mm}
        maxLength={2}
        onChange={makeChangeHandler(setMm, 2, yyyyRef, (value) => {
          if (!value) return true;
          const month = Number(value);
          if (Number.isNaN(month) || month < 0) return false;
          if (value.length === 1) return true;
          if (month < 1) return false;
          return month <= 12;
        })}
        onKeyDown={makeKeyDownHandler(ddRef)}
        onBlur={makeBlurPadHandler(setMm)}
        className={`w-11 ${segBase}`}
        aria-label="Month"
      />
      <span className="text-gray-500 select-none text-sm">/</span>
      <input
        ref={yyyyRef}
        type="text"
        inputMode="numeric"
        placeholder="YYYY"
        value={yyyy}
        maxLength={4}
        onChange={makeChangeHandler(setYyyy, 4, hhRef)}
        onKeyDown={makeKeyDownHandler(mmRef)}
        onBlur={handleBlur}
        className={`w-16 ${segBase}`}
        aria-label="Year"
      />
      <span className="text-gray-500 select-none text-xs mx-0.5">at</span>
      <input
        ref={hhRef}
        type="text"
        inputMode="numeric"
        placeholder="HH"
        value={hh}
        maxLength={2}
        onChange={makeChangeHandler(setHh, 2, miRef, (value) => {
          if (!value) return true;
          const hour = Number(value);
          if (Number.isNaN(hour) || hour < 0) return false;
          if (value.length === 1) return true;
          if (hour < 1) return false;
          return hour <= 12;
        })}
        onKeyDown={makeKeyDownHandler(yyyyRef)}
        onBlur={makeBlurPadHandler(setHh)}
        className={`w-11 ${segBase}`}
        aria-label="Hour in 12-hour format"
      />
      <span className="text-gray-500 select-none text-sm">:</span>
      <input
        ref={miRef}
        type="text"
        inputMode="numeric"
        placeholder="MM"
        value={mi}
        maxLength={2}
        onChange={makeChangeHandler(setMi, 2, periodRef, (value) => {
          if (!value) return true;
          const minute = Number(value);
          if (Number.isNaN(minute) || minute < 0) return false;
          if (value.length === 1) return true;
          return minute <= 59;
        })}
        onKeyDown={makeKeyDownHandler(hhRef)}
        onBlur={makeBlurPadHandler(setMi)}
        className={`w-11 ${segBase}`}
        aria-label="Minute"
      />
      <select
        ref={periodRef}
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
        onKeyDown={makeKeyDownHandler(miRef)}
        onBlur={handleBlur}
        className={`w-16 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 transition py-2.5 px-2 ${ring}`}
        aria-label="AM or PM"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
}
